import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@/shared/components/ui/Loaders/Loader";
import Conversations from "./Conversations";
import {
  useConnectMicrosoftConversations,
  useEnableMicrosoftTranscripts,
  useAccountConversationsBootstrap,
  useMicrosoftAdminConsentUrl,
  useMicrosoftConversationsStatus,
} from "../../hooks/useAccountConversations";
import logo from "@/assets/images/logo.jpg";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { Link } from "lucide-react";
import { MicrosoftLogo } from "@/assets/svg/MicrosoftLogo";
import { useGetMyChannels } from "@/hooks/useIntegration";

const ConversationsGate = () => {
  const { companyDomain } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const copyFeedbackTimeoutRef = useRef(null);
  const [adminConsentUrl, setAdminConsentUrl] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");

  const {
    data: statusResponse,
    isLoading: isStatusLoading,
    refetch: refetchStatus,
  } = useMicrosoftConversationsStatus();

  const status = statusResponse?.data;
  const { data: channelsResponse, isLoading: isChannelsLoading } = useGetMyChannels();
  const activeMailboxProviders = (channelsResponse?.result ?? [])
    .filter((account) => String(account.status || "").toLowerCase() === "active")
    .map((account) => String(account.provider || "").trim().toUpperCase());
  const hasConnectedMailbox = activeMailboxProviders.some((provider) =>
    ["GOOGLE", "GOOGLE_OAUTH", "OUTLOOK", "EXCHANGE", "MAIL"].includes(provider),
  );
  // Meetings come only from Microsoft, so offering it to a Gmail user makes no sense.
  const hasMicrosoftMailbox = activeMailboxProviders.some((provider) =>
    ["OUTLOOK", "EXCHANGE"].includes(provider),
  );
  const connectMutation = useConnectMicrosoftConversations();
  const enableTranscriptsMutation = useEnableMicrosoftTranscripts();
  const adminConsentUrlMutation = useMicrosoftAdminConsentUrl();
  const queryClient = useQueryClient();
  const wasSyncingRef = useRef(false);

  const returnPath = `/accounts/${encodeURIComponent(companyDomain || "")}/conversations`;

  const interactionsEnabled =
    (hasConnectedMailbox || (Boolean(status?.ready) && !status?.needsConsent)) && !!companyDomain;

  const {
    data: bootstrapResponse,
    isLoading: isBootstrapLoading,
  } = useAccountConversationsBootstrap({
    companyName: companyDomain,
    enabled: interactionsEnabled,
  });

  useEffect(() => {
    const consentResult = searchParams.get("ms_conversations");
    const adminConsentResult = searchParams.get("ms_admin_consent");

    if (consentResult === "success" || adminConsentResult === "success") {
      refetchStatus();
      const next = new URLSearchParams(searchParams);
      next.delete("ms_conversations");
      next.delete("ms_admin_consent");
      next.delete("message");
      next.delete("code");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refetchStatus]);

  const bootstrapData = bootstrapResponse?.data;
  const syncStatus = bootstrapData?.sync_status;
  const isSyncing = syncStatus === "queued" || syncStatus === "running";

  // The channel list is its own query; refresh it as the sync saves meetings, then mail.
  useEffect(() => {
    if (isSyncing || wasSyncingRef.current) {
      queryClient.invalidateQueries({ queryKey: ["accountInteractions", companyDomain] });
    }
    wasSyncingRef.current = isSyncing;
  }, [bootstrapData, companyDomain, isSyncing, queryClient]);

  const handleCopyAdminLink = async () => {
    let url = adminConsentUrl;
    if (!url) {
      const response = await adminConsentUrlMutation.mutateAsync({
        returnPath,
      });
      url = response?.data?.url || "";
      setAdminConsentUrl(url);
    }
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback("Link copied — send this to your Microsoft 365 admin.");
    } catch {
      setCopyFeedback(url);
    }

    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
    }
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setCopyFeedback("");
    }, 2500);
  };

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  if (isStatusLoading || isChannelsLoading) {
    return (
      <div className="w-full h-full min-h-[420px] flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const consentError =
    searchParams.get("ms_conversations") === "error"
      ? searchParams.get("message")
      : null;
  const consentErrorCode = searchParams.get("code");

  if (status?.needsConsent && !hasConnectedMailbox) {
    const isAccountMismatch =
      consentErrorCode === "MS_MICROSOFT_ACCOUNT_MISMATCH";
    const isAdminConsentRequired =
      consentErrorCode === "MS_ADMIN_CONSENT_REQUIRED" ||
      (consentError &&
        decodeURIComponent(consentError)
          .toLowerCase()
          .includes("admin consent"));

    return (
      <div className="w-full px-4 my-auto h-full">
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-5 text-center h-full justify-center">
          <div className="mx-auto flex items-center justify-center">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-gray-200 p-2">
              <img
                src={logo}
                alt="SalesWorx"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex w-12 shrink-0 items-center">
              <div className="h-0 flex-1 border-t border-dashed border-gray-300" />
              <span className="shrink-0 px-0.5 text-base font-medium leading-none text-gray-400">
                +
              </span>
              <div className="h-0 flex-1 border-t border-dashed border-gray-300" />
            </div>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-dashed border-gray-300 p-2">
              <MicrosoftLogo size={24} />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="text-lg font-semibold text-gray-800">
              Connect Microsoft for Conversations
            </h3>

            {consentError && (
              <p className="text-left text-xs text-error-500">
                {decodeURIComponent(consentError)}
              </p>
            )}

            {isAccountMismatch ? (
              <p className="text-sm leading-5 text-gray-600">
                Use the same Microsoft account as your SalesWorx login when
                connecting.
              </p>
            ) : (
              <p className="text-sm leading-5 text-gray-500">
                Sync your Outlook mail and Teams calendar to view interaction
                history instantly.
              </p>
            )}
          </div>

          <PrimaryButton
            className="h-11 w-full justify-center bg-secondary text-sm font-medium shadow-none"
            startContent={<MicrosoftLogo size={16} />}
            disabled={connectMutation.isPending}
            isLoading={connectMutation.isPending}
            onClick={() =>
              connectMutation.mutate({ returnPath, scopeMode: "core" })
            }
          >
            {connectMutation.isPending ? "Redirecting..." : "Connect Microsoft"}
          </PrimaryButton>

          {status?.needsAdminConsent && (
            <div className="flex w-full flex-col gap-2 border-t border-gray-200 pt-5 text-left">
              <p className="text-sm font-semibold text-gray-800">
                💡 Need Admin Approval?
              </p>
              {status?.canGrantAdminConsent ? (
                <>
                  <p className="text-sm leading-5 text-gray-500">
                    Copy the link below and send it to your Microsoft 365 admin
                    (e.g. admin@worxwide.com). They can approve SalesWorx for
                    your organization without a SalesWorx account. Once
                    approved, users can connect mail, calendar, and access
                    transcripts.
                  </p>
                  <SecondaryButton
                    title={
                      adminConsentUrlMutation.isPending
                        ? "Preparing link..."
                        : "Copy Admin Consent Link"
                    }
                    icon={<Link size={16} className="text-gray-700" />}
                    className="h-11 w-full justify-center border-gray-300 text-sm font-medium shadow-none"
                    disabled={adminConsentUrlMutation.isPending}
                    isLoading={adminConsentUrlMutation.isPending}
                    onClick={handleCopyAdminLink}
                  />
                  {copyFeedback && (
                    <p className="break-all text-xs text-success-500">
                      {copyFeedback}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm leading-5 text-gray-600">
                  Ask your Microsoft 365 admin to approve SalesWorx for your
                  organization before transcript access can be enabled.
                </p>
              )}
            </div>
          )}

          {isAdminConsentRequired && !status?.canGrantAdminConsent && (
            <p className="w-full text-left text-xs text-amber-700">
              Your IT admin must grant organization-wide permission in Microsoft
              before you can connect.
            </p>
          )}
        </div>
      </div>
    );
  }

  const isConversationsLoading =
    isBootstrapLoading ||
    (isSyncing && (bootstrapData?.interactions?.length ?? 0) === 0);

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-3 overflow-hidden">
      <div className="mx-4 mt-2 flex shrink-0 items-center gap-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {bootstrapData?.last_synced_at
            ? `Last synced ${new Date(bootstrapData.last_synced_at).toLocaleString()}`
            : "Not synced yet"}
        </p>
      </div>
      {status?.canEnableTranscripts && (
        <div className="mx-4 mt-2 flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-secondary-transparent bg-secondary-ultra-light dark:bg-gray-800 dark:border-gray-700 px-4 py-3">
          <p className="text-sm leading-5 text-gray-500 dark:text-gray-300">
            Your organization approved transcript access. Enable it on your
            account to view Teams meeting transcripts.
          </p>
          <PrimaryButton
            className="h-9 shrink-0 justify-center bg-secondary px-3 text-sm font-medium shadow-none"
            startContent={false}
            disabled={enableTranscriptsMutation.isPending}
            isLoading={enableTranscriptsMutation.isPending}
            onClick={() => enableTranscriptsMutation.mutate({ returnPath })}
          >
            {enableTranscriptsMutation.isPending
              ? "Redirecting..."
              : "Enable transcripts"}
          </PrimaryButton>
        </div>
      )}

      {isSyncing && (
        <div className="mx-4 flex shrink-0 items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="h-2 w-2 animate-pulse rounded-full bg-secondary" />
          Updating conversations in the background...
        </div>
      )}
      {(syncStatus === "failed" || bootstrapData?.sync_error) && (
        <div className="mx-4 shrink-0 text-xs text-error-500 dark:text-red-400">
          {bootstrapData?.sync_error || "Conversation update failed. Showing the last saved data."}
        </div>
      )}

      <div className="min-h-0 flex-1 basis-0 overflow-hidden">
        <Conversations
          companyName={companyDomain}
          contacts={bootstrapData?.contacts ?? []}
          interactions={bootstrapData?.interactions ?? []}
          summary={bootstrapData?.summary}
          sentiment={bootstrapData?.sentiment}
          bant={bootstrapData?.bant}
          isLoading={isConversationsLoading}
          isSummaryLoading={isBootstrapLoading || isSyncing}
          connectMeetings={
            status?.needsConsent && hasMicrosoftMailbox
              ? {
                  onConnect: () => connectMutation.mutate({ returnPath, scopeMode: "core" }),
                  isConnecting: connectMutation.isPending,
                }
              : null
          }
        />
      </div>
    </div>
  );
};

export default ConversationsGate;
